import moment from 'moment';
import {
  isStringAlphaNumericUnderscoreHyphen,
  isStringOnlyUnderscoreHyphen,
  isValidEmailRegex
} from '../../../core/utils';
import FundApi from '../../../core/apis/FundApi';
import { memoize } from 'lodash';

export const STEP = {
  fundDetails: 'fundDetails',
  addRelatedInvestors: 'addRelatedInvestors',
  addRelatedContacts: 'addRelatedContacts'
};

/**
 * Form level validation for new funds
 * @param {*} values Form values
 * @returns errors object
 */
export const validateFund = values => {
  const errors = {};
  if (values.startDate && values.endDate) {
    if (
      moment(values.startDate).startOf('day') >
      moment(values.endDate).startOf('day')
    ) {
      errors.startDate =
        'The Closing Date/As of date must be after the Start Date';

      return errors;
    }
  }
};

// react admin
export function validateFundIdRa(value, _allValue, fieldProps) {
  let errors = {};
  if (fieldProps.active) {
    return validateFundId(value);
  }
  return errors.id;
}

// rff
export function validateFundIdRff(value) {
  return validateFundId(value);
}

/*
 * Verify that Fund ID is unique and alphanumeric,
 * also check if ID is not made just from Underscore and Hyphens
 */
const validateFundId = memoize(async value => {
  const idValid = isStringAlphaNumericUnderscoreHyphen(value);
  const isValueOnlyUnderscoreHyphen = isStringOnlyUnderscoreHyphen(value);
  let error;

  if (!idValid) {
    error =
      'Fund ID must be unique and only contain numbers, letters, underscore and hyphen symbols without any spaces';
  } else if (!isValueOnlyUnderscoreHyphen) {
    error = 'Fund ID must contain at least one number or letter character';
  } else {
    try {
      const idExists = await FundApi.checkIdExists(value);
      if (idExists) {
        error = 'ID already exists';
      }
    } catch (e) {
      error = 'Cannot validate Fund ID';
    }
  }
  return error;
});

/**
 * Parse a comma separated list of emails as an array
 * @param {*} value comma separated list
 * @returns array of emails
 */
export const emailParser = value => {
  if (value && value.length > 0) {
    return value.split(';');
  }
  return [];
};

export const validateEmails = field => {
  let message = '';
  if (field && field.length > 0) {
    field.forEach(element => {
      if (!isValidEmailRegex(element) || element.length > 40) {
        message =
          'Please enter a valid email address or multiple email addresses separated by semicolon';
      }
    });
  }
  return message;
};

const maxSingleEmailAddressLength = 40;
export const validateSingleEmailFromArray = field => {
  let message = '';
  if (
    field === '' ||
    field === null ||
    field === undefined ||
    (Array.isArray(field) && field.length === 0)
  ) {
    return message;
  }

  if (!isValidEmailRegex(field) === true) {
    message = 'Please enter a valid email address';
  } else if (field[0] && field[0].length > maxSingleEmailAddressLength) {
    message =
      'Email address should not contain more than ' +
      maxSingleEmailAddressLength +
      ' characters.';
  }
  return message;
};

const maxSingleAddressLength = 150;
export const validateEmail = field => {
  let message;
  if (!isValidEmailRegex(field) === true) {
    message = 'Please enter a valid email address';
  } else if (field && field.length > maxSingleAddressLength) {
    message =
      'Email address should not contain more than ' +
      maxSingleAddressLength +
      ' characters.';
  }
  return message;
};

export const validateEmailAllowEmpty = field => {
  let message;
  if (
    field === '' ||
    field === null ||
    field === undefined ||
    (Array.isArray(field) && field.length === 0)
  ) {
    return message;
  }
  if (!isValidEmailRegex(field) === true) {
    message = 'Please enter a valid email address';
  } else if (field && field.length > maxSingleAddressLength) {
    message =
      'Email address should not contain more than ' +
      maxSingleAddressLength +
      ' characters.';
  }
  return message;
};

/**
 * Format a list of emails as a string (comma separated)
 * @param {*} value List of emails
 * @returns comma separated list of emails
 */
export const emailFormatter = value => {
  if (value && typeof value != 'string' && value.length > 0) {
    return value.reduce(
      (res, current) => res + (res !== '' ? ';' : '') + current,
      ''
    );
  }
  return '';
};
